import { format, isAfter, parseISO } from 'date-fns'

import {
  commentaryMessageTypes,
  dataWidgetTypes,
  commentaryTypes,
  dataWidgetProvider,
  pageTypes,
} from '../constants/common'
import { PageType } from '../types/servicesTypes/pageTypes'
import {
  type MatchItem,
  type OptaMatchDetails,
  type OptaMatchItem,
  type OptaMatchScores,
  PeriodId,
  TeamPosition,
  MatchCommentaryData,
  CommentaryData,
  OptaTeamStanding,
} from '../types/soccerDataTypes'

/**
 * Formats a date for matches request.
 * @param date - The date to be formatted.
 * @returns The formatted date string in the format "yyyy-MM-dd'T'HH:mm:ssXXX".
 */
export const formatDateForMatchesRequest = (date: Date) => format(date, "yyyy-MM-dd'T'HH:mm:ssXXX")

export const getSoccerTeamLogo = (id: string) =>
  `https://secure.cache.images.core.optasports.com/soccer/teams/75x75/uuid_${id}.png`

export const getLeaguePositionColor = (index: number) => {
  if (index < 4) {
    return 1
  }
  if (index === 4) {
    return 2
  }
  if (index === 5) {
    return 3
  }
  if (index < 15) {
    return 4
  }
  if (index === 15) {
    return 5
  }
  return 6
}

export const getGroupsPositionColor = (index: number) => {
  if (index < 2) {
    return 1
  }
  if (index === 2) {
    return 2
  }
  return 4
}

export const getMatchScore = (match: MatchItem) => {
  const dateISO = parseISO(match.startsAt.replace('Z', 'T'))
  const startsAt = new Date(dateISO)
  const isDateAfter = isAfter(startsAt, new Date())
  const homeScore = isDateAfter ? '-' : match.score?.home
  const awayScore = isDateAfter ? '-' : match.score?.away

  return { home: homeScore, away: awayScore }
}

export const getScore = (
  side: TeamPosition,
  scores?: OptaMatchScores,
  period: 'value' | 'fh' | 'sh' = 'value',
): number => {
  if (!scores) {
    return 0
  }
  if (period === 'fh') {
    return scores.ht?.[side] || 0
  }
  if (period === 'sh') {
    return (scores.total?.[side] || 0) - (scores.ht?.[side] || 0)
  }
  return scores.total?.[side] || 0
}

export const getMatchTime = (
  matchDetails: OptaMatchDetails,
  period: 'value' | 'fh' | 'sh' = 'value',
):
  | {
      min: number
      sec: number
    }
  | undefined => {
  if (!matchDetails) {
    return undefined
  }
  if (period === 'value') {
    if (typeof matchDetails.matchLengthMin === 'undefined' || typeof matchDetails.matchLengthSec === 'undefined') {
      return undefined
    }
    return {
      min: matchDetails.matchLengthMin,
      sec: matchDetails.matchLengthSec,
    }
  }
  const periodIndex = period === 'fh' ? 0 : 1
  if (!matchDetails.period?.[periodIndex]) {
    return undefined
  }
  return {
    min: matchDetails.period[periodIndex].lengthMin || 0,
    sec: matchDetails.period[periodIndex].lengthSec || 0,
  }
}

export const reduceMatches = (all: MatchItem[], { matchInfo, liveData }: OptaMatchItem): any[] => {
  const home = matchInfo.contestant?.find((contestant) => contestant.position === TeamPosition.Home)
  const away = matchInfo.contestant?.find((contestant) => contestant.position === TeamPosition.Away)
  if (!home || !away) {
    return all
  }

  const matchDetails = liveData?.matchDetails
  const match: MatchItem = {
    id: matchInfo.id,
    home,
    away,
    startsAt: `${matchInfo.date}${matchInfo.time || ''}`,
    time: getMatchTime(matchDetails),
    hasStarted: !matchDetails?.periodId || matchDetails?.periodId !== PeriodId.PreMatch,
    score: {
      home: getScore(TeamPosition.Home, matchDetails?.scores),
      away: getScore(TeamPosition.Away, matchDetails?.scores),
    },
    matchStatus: liveData?.matchDetails.matchStatus,
  }

  return [...all, match]
}

export const getCommentaryData = ({ matchInfo, messages }: MatchCommentaryData): Nullable<CommentaryData> => {
  if (!matchInfo) {
    return null
  }
  const home = matchInfo.contestant?.find((contestant) => contestant.position === TeamPosition.Home)
  const away = matchInfo.contestant?.find((contestant) => contestant.position === TeamPosition.Away)
  const message = messages[0].message

  return {
    matchId: matchInfo.id,
    startsAt: `${matchInfo.date}${matchInfo.time || ''}`,
    teamsIds: {
      home: home?.id,
      away: away?.id,
    },
    message,
  }
}

export const isGoalMessageType = (messageType: string) => {
  const goalTypes = [commentaryMessageTypes.GOAL, commentaryMessageTypes.PENALTY_GOAL, commentaryMessageTypes.OWN_GOAL]
  return goalTypes.some((type) => type === messageType)
}

export const getCurrentMatchScore = (commentaryData: CommentaryData, messageId: string) => {
  const goalMessages = commentaryData?.message?.filter((message) => isGoalMessageType(message.type))
  const hasTeamId = goalMessages?.every((messages) => messages.teamRef1 && messages.teamRef2)
  if (!goalMessages?.length || !hasTeamId) {
    return ''
  }
  const messageIndex = goalMessages.findIndex((message) => message.id === messageId)

  const score = goalMessages.slice(messageIndex).reduce(
    (score, message) => {
      const isOwnGoal = message.type === commentaryMessageTypes.OWN_GOAL
      const teamId = isOwnGoal ? message.teamRef2 : message.teamRef1
      const incrementKey = teamId === commentaryData?.teamsIds.home ? 'home' : 'away'
      return { ...score, [incrementKey]: score[incrementKey] + 1 }
    },
    { home: 0, away: 0 },
  )

  return score ? ` ${score.home}:${score.away}` : ''
}

export const createTeamPage = (team: OptaTeamStanding, tournamentCalendarId?: string): PageType => {
  const teamId = team.contestantId

  return {
    id: teamId,
    title: team.contestantName,
    name: '',
    shortTitle: '',
    categoryId: 'sport',
    pageType: pageTypes.TABS,
    withoutFavoriteIcon: true,
    dataExtension: {
      teamId,
    },
    logo: createMatchPageLogo(teamId),
    tabs: [
      {
        title: 'Matches',
        type: pageTypes.DATA_WIDGET,
        dataWidget: {
          dataProvider: dataWidgetProvider.STATS_PERFORM,
          dataType: 'Soccer',
          type: dataWidgetTypes.MATCHES,
          statsPerform: {
            tournamentCalendarId,
          },
        },
      },
      {
        title: 'Live Ticker',
        type: pageTypes.DATA_WIDGET,
        dataWidget: {
          dataProvider: dataWidgetProvider.STATS_PERFORM,
          dataType: 'Soccer',
          type: dataWidgetTypes.COMMENTARY,
          statsPerform: {
            tournamentCalendarId,
            contestantId: '',
            fixtureId: '',
            commentary: {
              type: commentaryTypes.AUTO,
            },
          },
        },
      },
    ],
  }
}

export const createMatchPageLogo = (teamId: string) => ({
  sources: [
    {
      url: getSoccerTeamLogo(teamId),
      format: 'Png',
      width: 75,
      height: 75,
      darkTheme: true,
    },
  ],
})
